import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  EventAvailable as BookIcon,
  Hotel as StayIcon,
} from '@mui/icons-material';
import { useInView } from '@/hooks/useInView';

interface StepCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  visible: boolean;
  delay: number;
}

const StepCard: React.FC<StepCardProps> = ({ icon, title, description, visible, delay }) => (
  <Box
    sx={{
      textAlign: 'center',
      px: 3,
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(24px)',
      transition: `opacity 600ms ease-out ${delay}ms, transform 600ms ease-out ${delay}ms`,
    }}
  >
    <Box
      sx={{
        width: 72,
        height: 72,
        borderRadius: '50%',
        bgcolor: '#fef3e8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        mb: 2,
      }}
    >
      {icon}
    </Box>
    <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280, mx: 'auto', lineHeight: 1.6 }}>
      {description}
    </Typography>
  </Box>
);

export const HowItWorksSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [ref, inView] = useInView({ threshold: 0.2 });

  const steps = [
    {
      icon: <SearchIcon sx={{ fontSize: 32, color: '#f59842' }} />,
      title: 'Search',
      description: 'Browse unique properties in destinations around the world.',
    },
    {
      icon: <BookIcon sx={{ fontSize: 32, color: '#f59842' }} />,
      title: 'Book',
      description: 'Reserve your stay instantly with secure, hassle-free booking.',
    },
    {
      icon: <StayIcon sx={{ fontSize: 32, color: '#f59842' }} />,
      title: 'Stay',
      description: 'Enjoy a memorable experience with your host.',
    },
  ];

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#fafafa' }} ref={ref}>
      <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          component="h2"
          sx={{ fontWeight: 700, textAlign: 'center', mb: 6 }}
        >
          How it works
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          {steps.map((step, index) => (
            <Grid item xs={12} sm={4} key={step.title}>
              <StepCard
                icon={step.icon}
                title={step.title}
                description={step.description}
                visible={inView}
                delay={index * 150}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};
